<template></template>

<script>
  import { v1 as uuidv1 } from 'uuid'
  import { lambdaAuthHeaders } from '@/../../shared/src/utils/encryption.js'
  import { getHashFromString } from '@/../../shared/src/utils.js'
  import { getOrCreateClient, recordLayoutPoco, recordLayoutMucho } from '@/../../shared/src/utils/agora.js'
  import { agoraTokenUrlBase, agoraRecordingUrlBase } from '@/../../shared/src/utils/constants.js'
  import axios from '@/../../shared/src/utils/axios.js'
  import AgoraRTC from 'agora-rtc-sdk-ng'


  if (process.env.NODE_ENV !== 'development') {
    AgoraRTC.setLogLevel(2)
  }

  export default {
    name: 'AgoraMixin',

    props: {
      'vidSize': {
        'type': String,
        'default': 'medium'
      }
    },

    data () {
      return {
        'localTracks': [],
        'showLoader': false,
        'options': {
          'appId': '23fdd085e839411aaf9054aa2849cee6'
        },
        'client': null,
        'localRecordingInfo': {
          'mode': 'individual',
          'resourceId': null,
          'sid': null,
          'token': null,
          'uid': null,
          'cname': null
        },
        'recControl': null
      }
    },

    computed: {

      localVideoTrack () {
        return (this.localTracks || []).find((track) => (track.trackMediaType === 'video'))
      },

      localAudioTrack () {
        return (this.localTracks || []).find((track) => track.trackMediaType === 'audio')
      },

      videoSizeClass () {
        const standardClass = {
          'medium': 'col-xs-12',
          'small': 'col-xs-6'
        }
        const conversationMode = {
          'medium': 'col-xs-6',
          'small': 'col-xs-3'
        }
        return this.isConversationMode ? conversationMode[this.vidSize] : standardClass[this.vidSize]
      },

      localTrackUserId () {
        return this.config.user.id
      },

      clientConnected () {
        return this.client && this.client.connectionState === 'CONNECTED'
      }

    },

    methods: {

      createClient (id, mode = 'rtc', codec = 'vp8') {
        this.client = getOrCreateClient(id, mode, codec)
        this.addClientEventListeners()
        this.logInfo({'type': 'agora/createClient', 'payload': {'client': this.client._clientId, 'config': this.client._config}})
        return this.client
      },

      setRole (role) {
        this.logInfo({'type': 'agora/setClientRole', 'payload': {'role': role}})
        return this.client.setClientRole(role)
      },

      addClientEventListeners () {
        this.client.on('user-published', this.remoteUserPublished)
        this.client.on('user-unpublished', this.remoteUserUnPublished)
        this.client.on('user-mute-updated', this.userMuted)
        this.client.on('user-left', this.userLeft)
        this.client.on('connection-state-change', this.connectionChanged)
      },

      remoteUserPublished () {},

      remoteUserUnPublished () {},

      userMuted () {},

      userLeft (user, reason) {
        // this.logInfo({'type': 'agora/userLeft', 'payload': {'user': user.uid, 'reason': reason}})
      },

      connectionChanged (current, previous) {
        this.logInfo({'type': 'agora/connectionStatusChanged', 'payload': {'current': current, 'previous': previous}})
      },

      getToken (channelIdOpt, userIdOpt) {
        return axios.get(`${agoraTokenUrlBase}/${channelIdOpt}/${userIdOpt}/`, {'headers': lambdaAuthHeaders(uuidv1())})
      },

      joinChannel (appId, channelId, userId) {
        if (this.client && ['CONNECTED', 'CONNECTING'].includes(this.client.connectionState)) {
          return Promise.resolve()
        }
        const appIdOpt = appId || this.options.appId
        const channelIdOpt = (channelId || this.config.classroom.event.slug).slice(-60)
        const userIdOpt = userId || this.config.user.id
        this.logInfo({'type': 'agora/joinChannel', 'payload': {'app': appIdOpt, 'channel': channelIdOpt, 'user': userIdOpt}})

        return this.getToken(channelIdOpt, userIdOpt)
          .then((resp) => {
            this.logInfo({'type': 'agora/tokenSuccess', 'payload': {'data': resp.data}})
            return this.client.join(appIdOpt, channelIdOpt, resp.data.token, userIdOpt)
          })
          .then((resp) => {
            this.logInfo({'type': 'agora/joinChannelSuccess', 'payload': {'app': appIdOpt, 'channel': channelIdOpt, 'user': userIdOpt}})
            return resp
          })
      },

      getLocalTracks () {
        const micId = this.mediaOpts.audio.deviceId ? this.mediaOpts.audio.deviceId.exact : undefined
        const camId = this.mediaOpts.video && this.mediaOpts.video.deviceId ? this.mediaOpts.video.deviceId.exact : undefined
        this.logInfo({'type': 'agora/getLocalTracks', 'payload': {'mediaOpts': this.mediaOpts}})
        if (this.mediaOpts.video === false) {
          return Promise.all([
            AgoraRTC.createMicrophoneAudioTrack({'microphoneId': micId})
          ])
          .catch((err) => {
            this.logError({'type': 'error/agoraGetLocalTracksError', 'payload': {'mediaOpts': this.mediaOpts, 'err': err}})
            return []
          })
        } else {
          return Promise.all([
              AgoraRTC.createMicrophoneAudioTrack({'microphoneId': micId}),
              AgoraRTC.createCameraVideoTrack({
                'cameraId': camId,
                'encoderConfig': {
                  'height': (this.mediaOpts.video.width / this.mediaOpts.video.aspectRatio),
                  'width': this.mediaOpts.video.width
                }
              })
            ])
            .catch((err) => {
              this.logError({'type': 'error/agoraGetLocalTracksError', 'payload': {'mediaOpts': this.mediaOpts, 'err': err}})
              return []
            })
        }
      },

      publishTracks (tracks) {
        this.logInfo({'type': 'agora/startPublishTracks', 'payload': {'tracks': tracks}})
        return this.client.publish(tracks)
          .then((data) => {
            this.logInfo({'type': 'agora/publishTracksSuccess', 'payload': {'tracks': tracks}})
            return data
          })
          .catch((err) => {
            if (err.code === 'TRACK_ALREADY_PUBLISHED') {
              return {}
            }
            return this.logError({'type': 'error/agoraPublishTracksError', 'payload': {'tracks': tracks, 'err': err}})
          })
      },

      setConfiguration (config, track = this.localVideoTrack) {
        if (track && track._enabled && config) {
          return track.setEncoderConfiguration(config)
            .catch(() => Promise.resolve())
        } else {
          return Promise.resolve()
        }
      },

      unPublishTracks (tracks) {
        this.logInfo({'type': 'agora/unpublishTracks', 'payload': {'tracks': tracks}})
        return this.client.unpublish(tracks)
          .then((data) => {
            return data
          })
          .catch((err) => {
            return this.logError({'type': 'error/agoraUnpublishTracks', 'payload': {'tracks': tracks, 'err': err}})
          })
      },

      stopLocalTracks () {
        this.logInfo({'type': 'agora/stopLocalTracks', 'payload': {'tracks': this.localTracks}})
        if (this.localTracks && this.localTracks.forEach) {
          return this.localTracks.forEach(this.stopTrack)
        }
        return Promise.resolve()
      },

      disconnect (callback = () => {}) {
        this.logInfo({'type': 'agora/disconnect'})
        this.stopLocalTracks()
         if (this.client && ['CONNECTED', 'CONNECTING'].includes(this.client.connectionState)) {
          return this.client.leave()
            .then(callback)
            .catch((err) => {
              this.logError({'type': 'errors/agoraClientLeaveError', 'payload': {'error': {'message': err.message, 'name': err.name}}})
            })
         }
      },

      stopTrack (track) {
        if (track === 'undefined') return Promise.resolve()

        if (track && track.stop) {
          track.stop()
        }
        if (track && track.close) {
          track.close()
        }
        this.logInfo({'type': 'agora/stopTrack', 'payload': {'track': track}})
        return Promise.resolve()
      },

      startRecording () {
        this.localRecordingInfo.cname = this.config.classroom.event.slug
        this.localRecordingInfo.uid = Math.abs(getHashFromString(this.config.classroom.event.slug))

        return this.acquire()
          .then((resp) => {
            this.localRecordingInfo.resourceId = resp.data.resourceId
            return this.getRecordingToken()
          })
          .then((resp) => {
            this.localRecordingInfo.token = resp.data.token
            return this.postRecordingStart()
          })
          .then((resp) => {
            this.localRecordingInfo.sid = resp.data.sid
            return this.localRecordingInfo
          })
          // .catch((err) => {
          //   console.log('err', err)
          //   this.agoraRecordingErrorStartEvent()
          // })
      },

      acquire () {
        const body = {
          'cname': this.localRecordingInfo.cname,
          'uid': this.localRecordingInfo.uid,
          'mode': this.localRecordingInfo.mode
        }
        const url = `${agoraRecordingUrlBase}/acquire/`
        return axios.post(url, body)
      },

      getRecordingToken () {
        return axios.get(`${agoraTokenUrlBase}/${this.localRecordingInfo.cname}/${this.localRecordingInfo.uid}/`, {'headers': lambdaAuthHeaders(uuidv1())})
      },

      postRecordingStart (recInfo = this.localRecordingInfo) {
        const url = `${agoraRecordingUrlBase}/start/`
        const layout = this.getCompositeRecordingLayout()
        const classUrl = window.location.href.replace(window.location.hash, '')
        return axios.post(url, {...recInfo, 'layout': layout, 'classUrl': classUrl})
      },

      getCompositeRecordingLayout () {
        const userIds = [this.client._joinInfo.uid, ...this.client.remoteUsers.map(u => u._uintid)]
        const layout = userIds.length > 3 ? recordLayoutMucho(userIds) : recordLayoutPoco(userIds)
        return layout.slice(0, userIds.length)
      },

      stopRecording (recInfo = this.localRecordingInfo) {
        const url = `${agoraRecordingUrlBase}/stop/`
        return axios.post(url, recInfo)
      },

      queryRecordingStatus (recInfo = this.localRecordingInfo) {
        const url = `${agoraRecordingUrlBase}/status/`
        return axios.post(url, recInfo)
      },

      startRecControl () {
        this.recControl = setInterval(() => {
          this.queryRecordingStatus(this.localRecordingInfo)
        }, 30000) // 30 seconds
      },

      stopRecControl () {
        if (this.recControl) {
          clearInterval(this.recControl)
        }
      },

      getQueryStatus (data) {
        return data.serverResponse.status
      }
    },

    beforeDestroy () {
      this.stopRecControl()
    }
  }
</script>


<docs></docs>
